<template>
  <div
    v-if="shouldShow && displayText"
    class="pointer-events-none absolute z-30 rounded-8 py-0.5 text-white backdrop-blur-sm"
    :class="[positionClass, qualityClass, compact ? 'px-1 text-body-r-12-16 max-w-[90%] truncate' : 'px-2 text-body-r-12-16']"
    :title="tooltipText"
    data-test="video-stream-stats-badge"
  >
    {{ displayText }}
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'

import { isShowVideoStreamStats, PreferenceKey, usePreferencesStore } from '@/entities/preference'

import { useLocalization } from '@/shared/composables/localization'
import { formatBitrateKbps, getResolutionQualityLabel } from '@/shared/utils/webrtc'

import { useRtcVideoStats } from '../composables/use-rtc-video-stats'

type VideoStreamStatsPreview = {
  width: number
  height: number
  fps?: number
}

/* eslint-disable vue/require-default-prop -- optional props omitted from defaults to satisfy exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  sessionId?: string
  variant: 'outbound' | 'inbound'
  trackId?: string
  compact?: boolean
  preview?: VideoStreamStatsPreview
}>(), {
  compact: false,
})
/* eslint-enable vue/require-default-prop */

const { sessionId, variant, trackId, compact, preview } = toRefs(props)
const { t } = useLocalization()
const { statsBySessionId } = useRtcVideoStats()
const { preferences } = usePreferencesStore()

const shouldShow = computed(() => isShowVideoStreamStats(preferences[PreferenceKey.SHOW_VIDEO_STREAM_STATS]))

const sessionStats = computed(() => {
  if (preview.value || !sessionId.value) return undefined
  return statsBySessionId.value.get(sessionId.value)
})

const outboundStats = computed(() => {
  if (!trackId.value) return sessionStats.value?.outboundByTrackId.values().next().value
  return sessionStats.value?.outboundByTrackId.get(trackId.value)
})

const inboundStats = computed(() => {
  if (!trackId.value) return undefined
  return sessionStats.value?.inboundByTrackId.get(trackId.value)
})

const displayText = computed(() => {
  if (preview.value) {
    const qualityLabel = getResolutionQualityLabel(preview.value.height)
    return `${qualityLabel} · ${preview.value.width}×${preview.value.height}`
  }

  if (variant.value === 'outbound' && outboundStats.value) {
    const { qualityLabel, bitrateKbps } = outboundStats.value
    if (compact.value || !bitrateKbps) return qualityLabel
    return `${qualityLabel} · ${formatBitrateKbps(bitrateKbps)}`
  }

  if (variant.value === 'inbound' && inboundStats.value) {
    const { height, packetLossPercent } = inboundStats.value
    const resolutionLabel = getResolutionQualityLabel(height)
    if (compact.value) return resolutionLabel
    return `${resolutionLabel} · ${t('common.videoStreamStats.packetLoss', { percent: packetLossPercent })}`
  }

  return ''
})

const tooltipText = computed(() => {
  if (preview.value) {
    const parts = [`${preview.value.width}×${preview.value.height}`]
    if (preview.value.fps) parts.push(`${preview.value.fps} FPS`)
    return parts.join(', ')
  }

  if (variant.value === 'outbound' && outboundStats.value) {
    const { width, height, bitrateKbps, fps, limitationReason } = outboundStats.value
    const parts = [`${width}×${height}`, formatBitrateKbps(bitrateKbps)]
    if (fps) parts.push(`${fps} FPS`)
    if (limitationReason && limitationReason !== 'none') {
      parts.push(t(`common.videoStreamStats.limitation.${limitationReason}`))
    }
    return parts.join(', ')
  }

  if (variant.value === 'inbound' && inboundStats.value) {
    const { width, height, qualityScore, qualityLabel, jitterMs, fps, packetLossPercent } = inboundStats.value
    const parts = [
      `${width}×${height}`,
      t(`common.videoStreamStats.${qualityLabel}`),
      t('common.videoStreamStats.qualityScore', { score: qualityScore }),
      t('common.videoStreamStats.packetLoss', { percent: packetLossPercent }),
    ]
    if (jitterMs) parts.push(`${t('common.videoStreamStats.jitter')}: ${Math.round(jitterMs)} ms`)
    if (fps) parts.push(`${fps} FPS`)
    return parts.join(', ')
  }

  return ''
})

const positionClass = 'left-2 bottom-2'

const qualityClass = computed(() => {
  if (variant.value === 'outbound') {
    return 'bg-black-400/80'
  }

  if (variant.value === 'inbound' && inboundStats.value) {
    const label = inboundStats.value.qualityLabel
    if (label === 'excellent' || label === 'good') return 'bg-positive-tints-500/80'
    if (label === 'fair') return 'bg-warning-tints-600/80'
    return 'bg-red-tints-600/80'
  }

  return 'bg-black/50'
})
</script>
